import { ActionModal } from '@kcftech/design-system';
import {
    ICorporationResponse,
    IUserCorporationChild,
} from '@kcftech/prometheustypescriptapiclient';
import { CorporationDropdown } from 'components/dropdown/CorporationDropdown';
import { Checkbox } from 'components/forms/Checkbox';
import { Label } from 'components/forms/Label';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useToggle } from 'custom_hooks/useToggle';
import { useMemo } from 'react';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';

const corporationRules: RegisterOptions = {
    required: {
        value: true,
        message: 'Please select a corporation',
    },
};

export type AddCorporationToUserModalProps = Readonly<{
    onCreate: (c: ICorporationResponse, isAdmin: boolean) => void;
    onClose: () => void;
    currentCorporation?: IUserCorporationChild;
}>;

type AddCorporationToUserForm = Readonly<{
    corporation: ICorporationResponse;
}>;

export function AddCorporationToUserModal({
    onCreate,
    onClose,
    currentCorporation,
}: AddCorporationToUserModalProps) {
    const isEditing = useMemo(() => currentCorporation !== undefined, [
        currentCorporation,
    ]);

    const [adminCheckboxChecked, toggleAdminCheckboxChecked] = useToggle();
    const {
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        control,
    } = useForm<AddCorporationToUserForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const onSave = (data: AddCorporationToUserForm) => {
        onCreate(data.corporation, adminCheckboxChecked);
    };

    return (
        <ActionModal
            onAction={handleSubmit(onSave)}
            actionable={(!isSubmitted || isValid) && !(isValidating || isSubmitting)}
            onClose={onClose}
            title={
                isEditing ? 'Edit Corporation Assignment' : 'Add Corporation Assignment'
            }
            actionButtonText="Save"
            closeButtonText="Cancel"
            height={400}
        >
            <>
                <ValidatableLabel
                    label="Corporation"
                    required={currentCorporation === undefined}
                    className="pb-30"
                    error={errors.corporation}
                >
                    {currentCorporation ? (
                        <span>{currentCorporation.CorporationName}</span>
                    ) : (
                        <Controller
                            name="corporation"
                            control={control}
                            rules={corporationRules}
                            render={({ field: { value, onChange } }) => (
                                <CorporationDropdown value={value} onChange={onChange} />
                            )}
                        />
                    )}
                </ValidatableLabel>
                <Label label="Is Admin?">
                    <Checkbox
                        checked={adminCheckboxChecked}
                        onChange={toggleAdminCheckboxChecked}
                    />
                </Label>
            </>
        </ActionModal>
    );
}
